import { type JSX, useCallback, useEffect, useState } from "react";

import type { Environment } from "../api/bench";
import { type StatsReport, fetchStats } from "../api/stats";
import { type UsageBar, UsageChart } from "./UsageChart";
import { MultiSelect } from "./MultiSelect";

/** The nav entry this panel answers to. */
export const STATS_VIEW = "__stats__";

const PERIODS = [
  { key: 0, label: "All time" },
  { key: 1, label: "Today" },
  { key: 7, label: "Last 7 days" },
  { key: 30, label: "Last 30 days" },
  { key: 90, label: "Last 90 days" },
] as const;

interface StatsDashboardProps {
  environment: Environment;
  /** Opens the list view filtered to one project, for "show me what these are". */
  onInspect: (projectId: string) => void;
}

/**
 * What the platform has been used for, across projects.
 *
 * Assembled from the two things the service can actually answer — the memories it holds, with
 * their timestamps and types, and the tokens it spent — because there is no audit log to draw a
 * true interaction history from. Every count says whether it is a total or a floor: the service
 * paginates without counts, so a figure that hit the page ceiling is labelled rather than
 * presented as complete.
 */
export function StatsDashboard({ environment, onInspect }: StatsDashboardProps): JSX.Element {
  const [days, setDays] = useState<number>(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [report, setReport] = useState<StatsReport | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setProblem(null);
    try {
      setReport(await fetchStats(days || undefined, selected.length ? selected : undefined));
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  }, [days, selected]);

  useEffect(() => {
    void load();
  }, [load]);

  const projects = report?.projects ?? [];
  const readable = projects.filter((project) => !project.problem);
  const offered = [...new Set([...environment.default_projects, ...projects.map((p) => p.project_id)])];

  return (
    <section className="panel">
      <header className="panel__header">
        <div>
          <h2>Usage</h2>
          <p className="muted">
            What the platform recorded and what it spent, per project. There is no audit log in
            the Memory Service, so this is what it holds &mdash; not a log of who called what.
          </p>
        </div>
        <span className="badge-row">
          {report?.capped && (
            <span className="flag flag--warn" title={report.note ?? undefined}>
              counts are floors
            </span>
          )}
          <button type="button" onClick={() => void load()} disabled={loading}>
            Refresh
          </button>
        </span>
      </header>

      {/* One row, above everything it scopes. */}
      <div className="filters">
        <label>
          <span>Period</span>
          <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
            {PERIODS.map((period) => (
              <option key={period.key} value={period.key}>
                {period.label}
              </option>
            ))}
          </select>
        </label>

        <MultiSelect
          label="Projects"
          options={offered}
          selected={selected}
          onChange={setSelected}
          allLabel={`All ${offered.length} projects`}
          searchPlaceholder="Find a project…"
        />

        {report && (
          <span className="muted">
            tenant <code>{report.tenant_id}</code>
          </span>
        )}
      </div>

      {problem && (
        <div className="notice notice--error">
          <strong>Could not read usage.</strong> {problem}
        </div>
      )}

      {report?.note && (
        <div className="notice notice--warning">
          <strong>These counts are floors, not totals.</strong> {report.note}
        </div>
      )}

      {report && (
        <div className={loading ? "chart-frame chart-frame--stale" : "chart-frame"}>
          <dl className="kpis">
            <div>
              <dt>Memories</dt>
              <dd>
                {report.memories.toLocaleString()}
                {report.capped && "+"}
              </dd>
            </div>
            <div>
              <dt>Tokens</dt>
              <dd>{report.total_tokens.toLocaleString()}</dd>
            </div>
            <div>
              <dt>LLM calls</dt>
              <dd>{report.llm_calls.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Projects</dt>
              <dd>{readable.length}</dd>
            </div>
          </dl>

          {report.daily.length > 0 && (
            <>
              <h3 className="section-heading">Memories written over time</h3>
              <UsageChart bars={dailyBars(report)} series={{ first: "Memories" }} orientation="vertical" />
            </>
          )}

          {Object.keys(report.by_type).length > 0 && (
            <>
              <h3 className="section-heading">By type</h3>
              <UsageChart bars={typeBars(report)} series={{ first: "Memories" }} orientation="horizontal" />
            </>
          )}

          <h3 className="section-heading">By project</h3>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Memories</th>
                  <th>Active</th>
                  <th>Superseded</th>
                  <th>Not embedded</th>
                  <th>Tokens</th>
                  <th>LLM calls</th>
                  <th>Last activity</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.project_id} className={project.problem ? "row--failed" : ""}>
                    <td>
                      <code>{project.project_id}</code>
                      {project.problem && (
                        <>
                          <br />
                          <span className="muted">{project.problem}</span>
                        </>
                      )}
                    </td>
                    <td>
                      {project.memories.toLocaleString()}
                      {project.capped && "+"}
                    </td>
                    <td>{project.active.toLocaleString()}</td>
                    <td>{project.superseded.toLocaleString()}</td>
                    <td>
                      {project.embedding_pending > 0 ? (
                        <span
                          className="flag flag--warn"
                          title="Not findable by recall until the embedding worker catches up"
                        >
                          {project.embedding_pending}
                        </span>
                      ) : (
                        0
                      )}
                    </td>
                    <td>{project.total_tokens.toLocaleString()}</td>
                    <td>{project.llm_calls.toLocaleString()}</td>
                    <td>{project.last_seen ? project.last_seen.slice(0, 16).replace("T", " ") : "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => onInspect(project.project_id)}
                      >
                        Inspect →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {Object.keys(report.by_agent).length > 0 && (
            <>
              <h3 className="section-heading">By agent</h3>
              <UsageChart bars={agentBars(report)} series={{ first: "Memories" }} orientation="horizontal" />
            </>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * One measure, so one series.
 *
 * Splitting a memory count into two stacked parts would mean inventing a distinction the reader
 * did not ask for -- so these are single-series bars, and the chart drops its legend accordingly.
 * The type mix is the *subject* of its own chart rather than a split inside another one.
 */
function dailyBars(report: StatsReport): UsageBar[] {
  return report.daily.map((point) => ({
    label: point.day,
    first: point.memories,
    second: 0,
    total: point.memories,
    extra: [{ label: "Types", value: Object.keys(point.by_type).length }],
  }));
}

function typeBars(report: StatsReport): UsageBar[] {
  return counted(report.by_type);
}

function agentBars(report: StatsReport): UsageBar[] {
  return counted(report.by_agent);
}

function counted(source: Record<string, number>): UsageBar[] {
  return Object.entries(source)
    .map(([label, count]) => ({ label, first: count, second: 0, total: count }))
    .sort((a, b) => b.total - a.total);
}
